/* eslint-disable max-lines */
import {ThunkMiddleware} from "redux-thunk";
import {PlayerReduxState} from "./types";
import {getCurrentAction, getSessionId, isResultStage} from "./selectors";
import passAction from "./actions/passAction";
import {PlayerReduxAction} from "./rootActions";
import {getSessionResultFeedbackQuery, sessionFinish, SessionResultFeedbackData} from "./graphql";
import {isAnyUserInputAction} from "./utils";
import {postMessageOpener} from "../../utils";
import {
  AvatarAction,
  AvatarEmotion,
  AvatarGesture,
  EducationSessionResult,
  FeedbackStatusEnumType,
} from "../../schema";
import {ApolloClient} from "@apollo/client";
import {finishScenarios} from "./actions/finishScenarios";
import i18n from "i18next";
import updateAdaptiveInputState from "./actions/updateAdaptiveInputState";

const rootMiddleware: ThunkMiddleware<PlayerReduxState, PlayerReduxAction> = function (store) {
  return function wrapDispatch(next) {
    return async function handleAction(action: PlayerReduxAction) {
      const prevState = store.getState();
      const result = next(action);

      if (["next", "back", "navigate", "startScenario", "unpause"].includes(action.type)) {
        const newState = store.getState();
        const toggleRecorder = newState.toggleRecorder;
        const avatarPlayback = newState.audioManager.getPlayback("player/avatar");
        const systemPlayback = newState.audioManager.getPlayback("player/system");
        const isSpeechMuted = newState.challenge.muteNonUserSpeech;
        const isResumingFromPause = action.type === "unpause" && prevState.onPause;

        if (!isResumingFromPause) {
          avatarPlayback?.abortPlay();
          systemPlayback?.abortPlay();
          store.dispatch({
            type: "resetVideoPlayback",
          });
        }

        const currentAction = getCurrentAction(newState);

        let turnMicOn = false;

        if (isResumingFromPause) {
          avatarPlayback?.resume();
          systemPlayback?.resume();
        } else {
          switch (currentAction?.__typename) {
            case "AvatarAction":
              avatarPlayback
                ?.play(currentAction.audio ?? undefined, {
                  gesture: currentAction.gesture,
                  emotion: currentAction.emotion,
                  volume: isSpeechMuted ? 0 : undefined,
                })
                .then(() => {
                  if (!currentAction.mediaViewRequired) store.dispatch(passAction());
                  else
                    store.dispatch({
                      type: "triggerVideoPlayback",
                    });
                });
              turnMicOn = currentAction.text === "" && !!currentAction.setMediaAsBackground;
              break;

            case "SystemAction":
              systemPlayback
                ?.play(currentAction.audio ?? undefined, {volume: isSpeechMuted ? 0 : undefined})
                .then(() => {
                  if (!currentAction.mediaViewRequired) store.dispatch(passAction());
                  else
                    store.dispatch({
                      type: "triggerVideoPlayback",
                    });
                });
              turnMicOn = currentAction.text === "" && !!currentAction.setMediaAsBackground;
              break;

            default:
              // noop
              break;
          }
        }

        if (currentAction && (isAnyUserInputAction(currentAction) || turnMicOn)) {
          toggleRecorder && toggleRecorder(true);
        }

        (async () => {
          const {isExternal, client} = newState;
          if (action.type === "next") {
            await store.dispatch(finishScenarios(prevState));
          }
          if (isResultStage(newState)) {
            await sendSessionFinish(newState, client, isExternal);

            if (newState.challenge.isSessionResultFeedbackEnabled && !newState.isFeedbackPlayed) {
              store.dispatch({type: "playSessionResultFeedback"});
              return;
            }
            store.dispatch({type: "finishChallenge"});
          }
        })();
      }

      if (action.type === "pause") {
        const newState = store.getState();
        const avatarPlayback = newState.audioManager.getPlayback("player/avatar");
        const systemPlayback = newState.audioManager.getPlayback("player/system");
        avatarPlayback?.pause();
        systemPlayback?.pause();
      }

      if (action.type === "skipSessionResultFeedback") {
        const newState = store.getState();
        const avatarPlayback = newState.audioManager.getPlayback("player/avatar");
        const systemPlayback = newState.audioManager.getPlayback("player/system");
        avatarPlayback?.abortPlay();
        systemPlayback?.abortPlay();
        store.dispatch({
          type: "resetVideoPlayback",
        });
      }

      if (action.type === "playAdaptiveResponse") {
        const newState = store.getState();
        const isSpeechMuted = newState.challenge.muteNonUserSpeech;
        const avatarPlayback = newState.audioManager.getPlayback("player/avatar");

        avatarPlayback?.abortPlay();
        if (newState.adaptiveInput) {
          const currentAdaptiveInput = newState.adaptiveInput;

          const currentResponse = currentAdaptiveInput.responses.find((r) => !r.played);
          if (currentResponse) {
            store.dispatch({
              type: "update",
              payload: {
                adaptiveInput: {
                  ...newState.adaptiveInput,
                  feedbackText: currentResponse?.text,
                },
              },
            });
            avatarPlayback
              ?.play(currentResponse.audioUrl ?? undefined, {
                volume: isSpeechMuted ? 0 : undefined,
              })
              .then(() => {
                store.dispatch(updateAdaptiveInputState(currentResponse.index));
              })
              .catch((reason) => {
                if (reason === "ABORT") {
                  return;
                }
                store.dispatch(updateAdaptiveInputState(currentResponse.index));
              });
          }
        }
      }

      if (action.type === "playSessionResultFeedback") {
        const state = store.getState();
        const sessionId = state.sessionId;
        if (!sessionId) return;

        const introAction: AvatarAction = {
          __typename: "AvatarAction",
          id: "feedback-intro",
          text: i18n.t("components.feedback.introMessage"),
          emotion: AvatarEmotion.IDLE,
          gesture: AvatarGesture.IDLE,
          isCustomAudio: false,
        };

        store.dispatch({type: "appendAction", payload: [introAction]});
        store.dispatch({type: "next"});


        const pollFeedback = async () => {
          const currentState = store.getState();
          const currentSessionId = currentState.sessionId;

          if (!currentSessionId || currentSessionId !== sessionId) {
            return;
          }

          try {
            const {data} = await currentState.client.query<SessionResultFeedbackData>({
              query: getSessionResultFeedbackQuery,
              variables: {sessionId: currentSessionId},
              fetchPolicy: "network-only",
            });

            const fb = data?.result?.resultFeedback;

            if (!fb || fb.feedbackStatus === FeedbackStatusEnumType.ERROR) {
              store.dispatch({type: "finishChallenge"});
              return;
            }

            if (fb.feedbackStatus === FeedbackStatusEnumType.READY) {
              const feedbackAction: AvatarAction = {
                __typename: "AvatarAction",
                id: "feedback-content",
                text: fb.text || "",
                audio: fb.audioUrl || undefined,
                emotion: AvatarEmotion.IDLE,
                gesture: AvatarGesture.NARRATIVE,
                isCustomAudio: true,
              };

              store.dispatch({type: "appendAction", payload: [feedbackAction]});
              store.dispatch({type: "next"});
            } else {
              setTimeout(pollFeedback, 1000);
            }
          } catch (e) {
            console.error("Error fetching session feedback", e);
            store.dispatch({type: "finishChallenge"});
          }
        };

        pollFeedback();
      }
      return result;
    };
  };
};

const sendSessionFinish = async (newState: PlayerReduxState, client: ApolloClient<object>, isExternal: boolean) => {
  const mutationResult = await client.mutate<{result: EducationSessionResult}>({
    mutation: sessionFinish,
    variables: {
      sessionId: getSessionId(newState),
    },
  });

  const result = mutationResult.data?.result;

  if (!result) {
    throw new Error("NoEducationSessionResult");
  }

  if (isExternal) {
    postMessageOpener({
      type: "Complete",
      passed: result.passed,
      score: result.score,
      time: result.time,
    });
  }
};
export default rootMiddleware;